import { formatDate, formatPhone } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Repeat } from "lucide-react";
import { useState } from "react";

import { PassportPanel } from "@/components/ownership/PassportPanel";
import { TransferDialog } from "@/components/ownership/TransferDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { isAdmin, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

import { usePropertyContext } from "./PropertyDetailPage";

/** Egasi, egalikni o'tkazish va PDF pasport (xodimlar uchun). */
export function OwnerTab() {
  const { property } = usePropertyContext();
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const admin = isAdmin(user);
  const [dialog, setDialog] = useState(false);
  const ready = property.status !== "draft";

  const pending = useQuery({
    queryKey: ["transfers", property.id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/{id}/transfer/", { params: { path: { id: property.id } } }),
      ),
    enabled: admin && ready,
  });
  const cancel = useMutation({
    mutationFn: async () => {
      const res = await api.DELETE("/api/properties/{id}/transfer/", {
        params: { path: { id: property.id } },
      });
      if (!res.response.ok) throw new Error("cancel");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["transfers", property.id] }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const transfer = pending.data?.[0];
  const owner = property.owner_detail;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <h3 className="mb-2 font-semibold">{t("owner.title")}</h3>
        {owner ? (
          <p>
            <strong>{owner.full_name || formatPhone(owner.phone)}</strong>{" "}
            <span className="text-muted-foreground">{formatPhone(owner.phone)}</span>
          </p>
        ) : property.owner_phone ? (
          <p>
            {formatPhone(property.owner_phone)}{" "}
            <span className="text-sm text-muted-foreground">— {t("owner.not_registered")}</span>
          </p>
        ) : (
          <p className="text-muted-foreground">{t("props.no_owner")}</p>
        )}
      </Card>

      {admin && (
        <Card>
          <h3 className="mb-2 font-semibold">{t("transfer.title")}</h3>
          {!ready && <p className="text-sm text-muted-foreground">{t("transfer.not_ready")}</p>}
          {ready && transfer && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-primary/10 p-3 text-sm">
              <span>
                {t("transfer.pending", {
                  phone: formatPhone(transfer.to_phone),
                  date: formatDate(transfer.expires_at),
                })}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {t("transfer.cancel")}
              </Button>
            </div>
          )}
          {ready && (
            <Button variant="outline" onClick={() => setDialog(true)}>
              <Repeat className="size-4" /> {transfer ? t("transfer.replace") : t("transfer.start")}
            </Button>
          )}
          <TransferDialog propertyId={property.id} open={dialog} onOpenChange={setDialog} />
        </Card>
      )}

      <Card>
        <h3 className="mb-2 font-semibold">{t("pass.title")}</h3>
        <PassportPanel propertyId={property.id} />
      </Card>
    </div>
  );
}
